import React from 'react';
import { AIAgentCapabilityConfig } from '@troia-v3/types';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Icon } from '@/shared/icons'
interface CalendarManagementConfigProps {
  config: AIAgentCapabilityConfig | undefined;
  onChange: (config: AIAgentCapabilityConfig) => void;
}

export const CalendarManagementConfig: React.FC<CalendarManagementConfigProps> = ({
  config,
  onChange
}) => {
  // Buscar usuários ativos para seleção
  const { data: users = [] } = useUsersDropdown({ status: 'active' });

  const isEnabled = config?.enabled || false;
  // config.config é Record<string, unknown>; validamos o tipo de cada campo.
  const allowedUserIds: string[] = Array.isArray(config?.config?.allowedUserIds)
    ? (config?.config?.allowedUserIds as string[])
    : [];
  const defaultDuration: number = typeof config?.config?.defaultDuration === 'number'
    ? config.config.defaultDuration
    : 60;

  const handleToggleEnabled = (enabled: boolean) => {
    onChange({
      capability: 'calendar_management',
      enabled,
      config: config?.config || {}
    });
  };

  const handleToggleUser = (userId: string) => {
    const newAllowedUserIds = allowedUserIds.includes(userId)
      ? allowedUserIds.filter(id => id !== userId)
      : [...allowedUserIds, userId];

    onChange({
      capability: 'calendar_management',
      enabled: isEnabled,
      config: {
        ...config?.config,
        allowedUserIds: newAllowedUserIds
      }
    });
  };

  const handleUpdateConfig = (key: string, value: any) => {
    onChange({
      capability: 'calendar_management',
      enabled: isEnabled,
      config: {
        ...config?.config,
        [key]: value
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* Header com Toggle */}
      <div
        className="p-4 rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="calendar" className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Gerenciamento de Calendário
              </h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => handleToggleEnabled(e.target.checked)}
                  className="w-4 h-4"
                  style={{ accentColor: 'var(--theme-primary)' }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {isEnabled ? 'Habilitado' : 'Desabilitado'}
                </span>
              </label>
            </div>
            <p
              className="text-xs"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Permite que o agente verifique disponibilidade e agende reuniões no calendário
            </p>
          </div>
        </div>
      </div>

      {/* Configurações (apenas se habilitado) */}
      {isEnabled && (
        <div className="space-y-4">
          {/* Usuários Permitidos */}
          <div>
            <label
              className="block text-xs mb-2 font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Usuários Permitidos
            </label>
            <p
              className="text-xs mb-3"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Selecione os usuários cujos calendários o agente pode acessar
            </p>

            <div
              className="rounded-lg border max-h-60 overflow-y-auto"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
              }}
            >
              {users.map((user) => {
                const isSelected = allowedUserIds.includes(user.id);

                return (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 px-4 py-3 border-b cursor-pointer transition-colors"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                      style={{
                        borderColor: isSelected
                          ? 'var(--theme-primary)'
                          : 'var(--theme-card-border)',
                        backgroundColor: isSelected
                          ? 'var(--theme-primary)'
                          : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <Icon name="check" className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleUser(user.id)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <p
                        className="text-sm font-medium"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        {user.name}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {user.email}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {allowedUserIds.length === 0 && (
              <p
                className="text-xs mt-2"
                style={{ color: 'var(--theme-warning-color)' }}
              >
                ⚠️ Nenhum usuário selecionado. O agente não poderá acessar calendários.
              </p>
            )}
          </div>

          {/* Duração Padrão de Reuniões */}
          <div>
            <label
              className="block text-xs mb-2 font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Duração Padrão de Reuniões
            </label>
            <div className="flex items-center gap-3">
              <Icon name="clock" className="w-4 h-4"
                style={{ color: 'var(--theme-muted-text)' }} />
              <select
                value={defaultDuration}
                onChange={(e) => handleUpdateConfig('defaultDuration', parseInt(e.target.value))}
                className="flex-1 h-[38px] px-3 py-2 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={60}>1 hora</option>
                <option value={90}>1 hora e 30 minutos</option>
                <option value={120}>2 horas</option>
              </select>
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Duração que será sugerida quando não especificada
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
